import { Check } from "lucide-react";
import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";

import type { MissionTask } from "@/campaigns/modules/missions/types";
import {
  formatProgressValue,
  isCountableProgress,
  parseProgressValue,
  progressRatio,
} from "@/campaigns/modules/missions/types";
import { formatPrizeTitle } from "@/utils/helpers";
import { TurnoverContributionNote } from "@/components/campaigns/campaign-contribution-rates";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { PrizeText } from "@/components/prize-text";

/**
 * Visual state of one task row inside a mission card:
 * - `done`      — completed: a gold "Done" badge, the title dimmed, no meter.
 * - `highlight` — THE task to do now (sequential missions): the figure in a solid active badge,
 *                 the meter's knob breathing.
 * - `neutral`   — active alongside others (free-order missions) or no per-player state yet: the
 *                 figure in a ghost badge, a still meter.
 * - `upcoming`  — not yet available: dimmed, "Up next", no meter.
 *
 * Drawn in the jackpot card's language rather than as boxed rows: the rows sit straight on the
 * card, divided by hairlines; the title is the silver medal gradient; the state is a Badge at
 * the far end; progress is the ceiling scale at row size (a hairline track, a fill running from
 * faint to the active colour, a glowing knob — ticks on the track for step tasks). Progress is
 * the `--active` token throughout: the state the rung badge and the card's own glow speak.
 */
export type MissionTaskVariant = "done" | "highlight" | "neutral" | "upcoming";

/** Discrete totals up to this render as pips — "2 of 5" reads as two filled
 *  segments, so the count survives without any digits. Larger (or fractional
 *  money) totals fall back to the continuous bar. */
const MAX_PIPS = 8;

/** The meter: the jackpot ceiling scale at row size (`.task-meter-*`, index.css). `steps` puts a
 *  tick per step on the track, lit once reached — "2 of 5" reads without digits. */
const TaskMeter = ({
  ratio,
  steps,
  current,
  live,
  label,
}: {
  ratio: number;
  steps?: number;
  current?: number;
  /** The task to do now: the knob breathes. */
  live: boolean;
  label?: string;
}) => {
  // 2% floor keeps started progress visible; zero shows the bare track.
  const pct = ratio > 0 ? Math.max(2, Math.round(ratio * 100)) : 0;
  return (
    <div
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn("task-meter", live && "is-current")}
      style={{ "--meter-pct": pct } as CSSProperties}
    >
      <div className="task-meter-track" />
      <div className="task-meter-fill" />
      {steps != null &&
        Array.from({ length: steps - 1 }, (_, i) => (
          <span
            key={i}
            className={cn(
              "task-meter-tick",
              current != null && i + 1 <= current && "is-on"
            )}
            style={{ left: `${((i + 1) / steps) * 100}%` }}
          />
        ))}
      {/* No knob until there is progress: a glowing marker at 0% reads as "started". The bare
          track (and its ticks) already says what is left to do. */}
      {pct > 0 && <div className="task-meter-knob" />}
    </div>
  );
};

/** Per-task reward (paid on task completion) — distinct from the mission
 *  prize; without this line a configured task reward would be invisible.
 *  Styled like the mission-level RewardTitle (gold, bold, no pill border) so
 *  both reward tiers read as the same thing, just sized for the row. */
const TaskPrizeChip = ({ task }: { task: MissionTask }) => {
  const { t } = useTranslation();
  const [first, ...rest] = task.prizes;
  if (!first) return null;
  const title = formatPrizeTitle(first.title, t);
  return (
    // Never truncated: the prize is the row's priority content (CLAUDE.md) — it
    // shrinks, then glides on its one row.
    <PrizeText size="xs" weight="bold" fit="marquee" className="min-w-0">
      {rest.length > 0 ? `${title} +${rest.length}` : title}
    </PrizeText>
  );
};

/** "23 / 50" — the task's figure, in the state badge at the far end of the row. Also the
 *  fallback for progress values that don't parse as numbers (a backend sending pre-formatted
 *  labels). Solid active on the task to do now, a ghost chip otherwise. */
const TaskFigure = ({ task, live }: { task: MissionTask; live: boolean }) => {
  if (!task.progress || !task.progress.current.trim()) return null;
  return (
    <Badge
      variant={live ? "solid" : "ghost"}
      color={live ? "active" : "neutral"}
      className="shrink-0"
    >
      {`${formatProgressValue(task.progress.current)} / ${formatProgressValue(task.progress.total)}`}
    </Badge>
  );
};

export const MissionTaskRow = ({
  task,
  variant,
  showProgress = true,
  showContributionNote = false,
  density = "default",
}: {
  task: MissionTask;
  variant: MissionTaskVariant;
  /** `dock`: the overlay's narrow column — the task's prize gets its own line under the
   *  title, so the prize keeps the row's whole width instead of sharing it with the title and
   *  the figure. */
  density?: "default" | "dock";
  /** Whether to draw progress (meter / figure) at all. Off for the rows of a LOCKED
   *  rung: the mapper sends `{"current": "0.00", total}` for every unstarted task, so without
   *  this the collapsed locked card would show a column of empty tracks for tasks the player
   *  cannot touch yet. */
  showProgress?: boolean;
  /** Whether this row belongs to a mission the player can work on NOW.
   *
   *  Explicit, and off by default, because the row cannot infer it. `variant` is a
   *  styling choice — `CollapsedCard` passes `"neutral"` for every task of a *locked*
   *  mission — and progress is no help either (see `showProgress`). Only the caller knows
   *  the mission's rung. Defaulting to `false` means a new call site that forgets shows
   *  nothing, rather than showing it in the wrong place. */
  showContributionNote?: boolean;
}) => {
  const { t } = useTranslation();

  const active = variant !== "done" && variant !== "upcoming";
  const progressing = active && showProgress;
  const total = parseProgressValue(task.progress?.total);
  const current = parseProgressValue(task.progress?.current);
  // A one-shot task (total 1) is countable here too — it gets the same figure + meter as its
  // siblings (`isCountableProgress` stays strict for the other surfaces that use it).
  const countable =
    isCountableProgress(task.progress) || (total !== null && total === 1);
  const ratio = countable ? progressRatio(task.progress) : null;

  // Every countable task gets the same anatomy, one-shot tasks (total = 1, e.g. "10x Win")
  // included: figure + meter (a single step). Without them an open one-shot task was a bare title
  // — out of place among its siblings — since the row lost the icon chip and the current-row tint
  // that used to carry its state.
  const useMeter = progressing && ratio !== null;
  // Step tasks up to MAX_PIPS get a tick per step on the meter.
  const steps =
    useMeter &&
    total !== null &&
    current !== null &&
    Number.isInteger(total) &&
    total <= MAX_PIPS
      ? total
      : undefined;
  // Unparseable-but-present progress (either side): fall back to the text
  // figure so a backend sending pre-formatted labels still surfaces them.
  const useTextFallback =
    progressing &&
    task.progress !== null &&
    task.progress.current.trim() !== "" &&
    ratio === null;
  const live = variant === "highlight";
  // A one-shot task (total 1 — a deposit, or a count of one) is done or not done: its
  // "0 / 1" says nothing the title and the meter don't, and reads as a count of one (Alexander,
  // 2026-10-16). Its figure goes; the meter stays. A true/false treatment is to come.
  const oneShot = total === 1;
  const prizeOwnLine = density === "dock";

  return (
    <div
      className={cn(
        // Straight on the card, hairline between rows (the jackpot card's "last win" divider).
        "relative flex w-full flex-col gap-2.5 py-3 first:pt-1 last:pb-1 [&:not(:first-child)]:border-t [&:not(:first-child)]:border-foreground/[0.06]",
        variant === "upcoming" && "opacity-50"
      )}
    >
      <div className="flex w-full items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              "text-task-sheen truncate font-heading text-[12px] leading-4 font-semibold lg:text-[14px] lg:leading-5",
              (variant === "done" || variant === "upcoming") && "opacity-60"
            )}
          >
            {task.title}
          </span>
          {!prizeOwnLine && <TaskPrizeChip task={task} />}
        </span>
        {variant === "done" ? (
          <Badge
            variant="tonal"
            color="prizes"
            icon={<Check strokeWidth={3} />}
            className="shrink-0"
          >
            {t("missions.taskDone", "Done")}
          </Badge>
        ) : variant === "upcoming" ? (
          <Badge variant="ghost" color="locked" className="shrink-0">
            {t("missions.taskUpNext", "Up next")}
          </Badge>
        ) : (useMeter && !oneShot) || useTextFallback ? (
          <TaskFigure task={task} live={live} />
        ) : null}
      </div>
      {prizeOwnLine && <TaskPrizeChip task={task} />}
      {useMeter && ratio !== null && (
        <TaskMeter
          ratio={ratio}
          steps={steps}
          current={current !== null ? Math.floor(current) : undefined}
          live={live}
          label={
            steps != null
              ? `${formatProgressValue(task.progress!.current)} ${t(
                  "missions.ofTotal",
                  "of {{total}}",
                  { total: formatProgressValue(task.progress!.total) }
                )}`
              : undefined
          }
        />
      )}
      {/* Contribution rates only ever apply to turnover, so this is the one task kind
          where a bar can move slower than the wagers behind it — and the moment the player
          asks why. Two conditions, because they know different things: the caller says
          whether the mission is one the player can work on, and `active` says whether this
          particular task is still open. Renders nothing unless the campaign also has a
          reduced rate configured. */}
      {showContributionNote && active && task.kind === "turnover" && (
        <TurnoverContributionNote />
      )}
    </div>
  );
};
